import React, { useState, useEffect, useRef } from 'react';

const OceanMode = ({ audioContext, isSoundEnabled }) => {
  const [bubbles, setBubbles] = useState([]);
  const [fish, setFish] = useState([]);
  const [splashes, setSplashes] = useState([]);
  const [isCurrentActive, setIsCurrentActive] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const [lastRClickTime, setLastRClickTime] = useState(0);
  const [rClickCount, setRClickCount] = useState(0);
  const mouseRef = useRef({ x: 0, y: 0 });
  const handlersRef = useRef({});
  const animFrameRef = useRef(null);
  const hintTimerRef = useRef(null);

  useEffect(() => {
    hintTimerRef.current = setTimeout(() => setShowHint(false), 6000);
    return () => clearTimeout(hintTimerRef.current);
  }, []);

  // ─── AUDIO ────────────────────────────────────────────────────────────────
  const playBubbleSound = () => {
    if (!audioContext || !isSoundEnabled) return;
    const now = audioContext.currentTime;
    const gain = audioContext.createGain();
    const osc = audioContext.createOscillator();
    const filter = audioContext.createBiquadFilter();
    osc.connect(filter); filter.connect(gain); gain.connect(audioContext.destination);
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(800 + Math.random() * 400, now);
    filter.Q.value = 8;
    osc.type = 'sine';
    osc.frequency.setValueAtTime(400 + Math.random() * 300, now);
    osc.frequency.exponentialRampToValueAtTime(600 + Math.random() * 400, now + 0.15);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.06, now + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
    osc.start(now); osc.stop(now + 0.2);
  };

  const playFishSound = () => {
    if (!audioContext || !isSoundEnabled) return;
    const now = audioContext.currentTime;
    // Soft water "blip"
    const gain = audioContext.createGain();
    const osc1 = audioContext.createOscillator();
    const osc2 = audioContext.createOscillator();
    const filter = audioContext.createBiquadFilter();
    osc1.connect(filter); osc2.connect(filter);
    filter.connect(gain); gain.connect(audioContext.destination);
    filter.type = 'lowpass'; filter.frequency.value = 600;
    osc1.type = 'sine'; osc2.type = 'sine';
    osc1.frequency.setValueAtTime(180, now);
    osc2.frequency.setValueAtTime(270, now);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.05, now + 0.08);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
    osc1.start(now); osc1.stop(now + 0.65);
    osc2.start(now); osc2.stop(now + 0.65);
  };

  const playSplashSound = () => {
    if (!audioContext || !isSoundEnabled) return;
    const now = audioContext.currentTime;
    // Deep underwater bloop
    const bufSize = audioContext.sampleRate * 0.6;
    const buf = audioContext.createBuffer(1, bufSize, audioContext.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufSize * 0.12));
    const src = audioContext.createBufferSource();
    src.buffer = buf;
    const filter = audioContext.createBiquadFilter();
    filter.type = 'bandpass'; filter.frequency.value = 350; filter.Q.value = 0.7;
    const gain = audioContext.createGain();
    src.connect(filter); filter.connect(gain); gain.connect(audioContext.destination);
    gain.gain.setValueAtTime(0.45, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.55);
    // Ethereal bio-ping (descending sine)
    const osc = audioContext.createOscillator(); const og = audioContext.createGain();
    osc.connect(og); og.connect(audioContext.destination);
    osc.type = 'sine'; osc.frequency.setValueAtTime(520, now);
    osc.frequency.exponentialRampToValueAtTime(160, now + 1.0);
    og.gain.setValueAtTime(0, now); og.gain.linearRampToValueAtTime(0.07, now + 0.06);
    og.gain.exponentialRampToValueAtTime(0.001, now + 1.0);
    osc.start(now); osc.stop(now + 1.1);
    src.start(now); src.stop(now + 0.65);
  };

  const playCurrentSound = (active) => {
    if (!audioContext || !isSoundEnabled) return;
    if (active) {
      if (audioContext.state === 'suspended') audioContext.resume();
      const now = audioContext.currentTime;
      const bufSize = audioContext.sampleRate * 2;
      const buf = audioContext.createBuffer(1, bufSize, audioContext.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < bufSize; i++) data[i] = Math.random() * 2 - 1;
      const src = audioContext.createBufferSource();
      src.buffer = buf; src.loop = true;
      const filter = audioContext.createBiquadFilter();
      filter.type = 'lowpass'; filter.frequency.value = 300;
      const gain = audioContext.createGain();
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.25, now + 0.8);
      src.connect(filter); filter.connect(gain); gain.connect(audioContext.destination);
      src.start(now);
      window._oceanCurrentSrc = { src, gain };
    } else {
      if (window._oceanCurrentSrc) {
        const { gain } = window._oceanCurrentSrc;
        const now = audioContext.currentTime;
        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(gain.gain.value, now);
        gain.gain.linearRampToValueAtTime(0, now + 0.4);
        setTimeout(() => { try { window._oceanCurrentSrc.src.stop(); } catch(e){} window._oceanCurrentSrc = null; }, 500);
      }
    }
  };

  // ─── ANIMATION LOOP ────────────────────────────────────────────────────────
  useEffect(() => {
    const tick = () => {
      // Animate bubbles upward
      setBubbles(prev => prev
        .map(b => ({
          ...b,
          y: b.y - b.speed,
          x: b.x + Math.sin(Date.now() / 600 + b.phase) * 0.4,
          opacity: Math.max(0, b.opacity - (b.y < 80 ? 0.02 : 0))
        }))
        .filter(b => b.y > -20 && b.opacity > 0)
      );

      // Animate fish
      setFish(prev => prev.map(f => {
        let nx = f.x + f.vx * (isCurrentActive ? 2.2 : 1);
        let ny = f.y + f.vy;
        const cxWrap = window.innerWidth + 80;
        if (nx > cxWrap) nx = -80;
        if (nx < -80) nx = cxWrap;
        if (ny > window.innerHeight - 60) ny = window.innerHeight - 80;
        if (ny < 60) ny = 80;
        // School cohesion towards leader
        const dvx = f.vx + (Math.random() - 0.5) * 0.06;
        const dvy = f.vy + (Math.random() - 0.5) * 0.04;
        const speed = Math.sqrt(dvx*dvx + dvy*dvy);
        const maxS = f.maxSpeed || 1.4;
        return { ...f, x: nx, y: ny, vx: (dvx/speed)*Math.min(speed, maxS), vy: (dvy/speed)*Math.min(speed*0.4, 0.5) };
      }));

      // Move bubbles with current
      if (isCurrentActive) {
        setBubbles(prev => prev.map(b => ({ ...b, x: b.x + 1.5 })));
      }

      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isCurrentActive]);

  // ─── INTERACTION ──────────────────────────────────────────────────────────
  handlersRef.current = {
    handleMouseDown: (e) => {
      if (e.button === 0) {
        // Spawn school of fish
        const count = 5 + Math.floor(Math.random() * 4);
        const baseVx = (Math.random() > 0.5 ? 1 : -1) * (0.8 + Math.random() * 0.6);
        const hue = Math.floor(Math.random() * 360);
        const newFish = Array.from({ length: count }).map((_, i) => ({
          id: Date.now() + i,
          x: e.clientX + (Math.random() - 0.5) * 60,
          y: e.clientY + (Math.random() - 0.5) * 40,
          vx: baseVx + (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.4,
          size: 10 + Math.random() * 8,
          hue: hue + (Math.random() - 0.5) * 30,
          maxSpeed: 1 + Math.random() * 0.8,
          flip: baseVx < 0
        }));
        setFish(prev => [...prev, ...newFish]);
        setShowHint(false);
        playFishSound();
      }
      if (e.button === 2) {
        // Spawn bubbles
        const now = Date.now();
        const tSince = now - lastRClickTime;
        if (tSince < 350 && rClickCount === 1) {
          // Double right-click: splash + rainbow
          const id = Date.now();
          setSplashes(prev => [...prev, { id, x: e.clientX, y: e.clientY }]);
          setTimeout(() => setSplashes(prev => prev.filter(s => s.id !== id)), 2500);
          playSplashSound();
          setRClickCount(0);
        } else {
          setRClickCount(1);
          setTimeout(() => setRClickCount(0), 350);
          // Single right click: bubbles
          const newBubbles = Array.from({ length: 6 + Math.floor(Math.random() * 5) }).map((_, i) => ({
            id: Date.now() + i,
            x: e.clientX + (Math.random() - 0.5) * 30,
            y: e.clientY,
            size: 4 + Math.random() * 10,
            speed: 0.6 + Math.random() * 1.2,
            phase: Math.random() * Math.PI * 2,
            opacity: 0.7 + Math.random() * 0.3
          }));
          setBubbles(prev => [...prev, ...newBubbles]);
          playBubbleSound();
        }
        setLastRClickTime(now);
        setShowHint(false);
      }
      if (e.button === 1) {
        e.preventDefault();
        setIsCurrentActive(true);
        playCurrentSound(true);
      }
    },
    handleMouseUp: (e) => {
      if (e.button === 1) {
        setIsCurrentActive(false);
        playCurrentSound(false);
      }
    }
  };

  useEffect(() => {
    const down = (e) => handlersRef.current.handleMouseDown(e);
    const up = (e) => handlersRef.current.handleMouseUp(e);
    const ctx = (e) => e.preventDefault();
    window.addEventListener('mousedown', down);
    window.addEventListener('mouseup', up);
    window.addEventListener('contextmenu', ctx);
    return () => {
      window.removeEventListener('mousedown', down);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('contextmenu', ctx);
    };
  }, []);

  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', zIndex: 99990, pointerEvents: 'none' }}>
      <style>{`
        @keyframes bubbleRise {
          0% { transform: scale(0); opacity: 0; }
          10% { transform: scale(1); opacity: 0.8; }
          100% { opacity: 0; }
        }
        @keyframes bioGlow {
          0% { transform: scale(0); opacity: 0; }
          25% { opacity: 1; }
          100% { transform: scale(3); opacity: 0; }
        }
        @keyframes bioRipple {
          0% { transform: scale(0); opacity: 0.9; }
          100% { transform: scale(4.5); opacity: 0; }
        }
        @keyframes bioParticle {
          0% { transform: translateY(0) scale(1); opacity: 1; }
          100% { transform: translateY(-70px) scale(0); opacity: 0; }
        }
        @keyframes fishSwim {
          0%,100% { transform: translateY(0px); }
          50% { transform: translateY(-3px); }
        }
        @keyframes hintFadeIn {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes currentFlow {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
      `}</style>

      {/* Ambient underwater tint */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none',
        background: isCurrentActive
          ? 'linear-gradient(180deg, rgba(0,80,140,0.08) 0%, rgba(0,120,180,0.12) 100%)'
          : 'linear-gradient(180deg, rgba(0,60,120,0.04) 0%, rgba(0,100,160,0.07) 100%)',
        transition: 'background 0.8s ease'
      }} />

      {/* Current flow lines */}
      {isCurrentActive && [...Array(6)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute', height: '2px', width: '120px',
          top: `${15 + i * 14}%`, left: '-120px',
          background: 'linear-gradient(90deg, transparent, rgba(100,200,255,0.4), transparent)',
          animation: `currentFlow 1.8s linear ${i * 0.3}s infinite`,
          backgroundSize: '200% 100%',
          borderRadius: '2px', pointerEvents: 'none'
        }} />
      ))}

      {/* Bubbles */}
      <svg style={{ position: 'absolute', width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
        {bubbles.map(b => (
          <g key={b.id} transform={`translate(${b.x}, ${b.y})`} opacity={b.opacity}>
            <circle r={b.size} fill="none" stroke="rgba(150,220,255,0.9)" strokeWidth="1.2" />
            <circle r={b.size * 0.3} cx={-b.size * 0.25} cy={-b.size * 0.3}
              fill="rgba(255,255,255,0.6)" />
          </g>
        ))}
      </svg>

      {/* Fish */}
      <svg style={{ position: 'absolute', width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
        <defs>
          <filter id="fishGlow">
            <feGaussianBlur stdDeviation="2" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        {fish.map(f => (
          <g key={f.id} transform={`translate(${f.x}, ${f.y}) scale(${f.flip ? -1 : 1}, 1)`}
             style={{ animation: 'fishSwim 0.8s ease-in-out infinite' }} filter="url(#fishGlow)">
            {/* Body */}
            <ellipse rx={f.size} ry={f.size * 0.45} fill={`hsla(${f.hue}, 80%, 65%, 0.9)`} />
            {/* Tail */}
            <polygon points={`${-f.size},0 ${-f.size*1.6},${-f.size*0.5} ${-f.size*1.6},${f.size*0.5}`}
              fill={`hsla(${f.hue}, 70%, 50%, 0.8)`} />
            {/* Eye */}
            <circle cx={f.size * 0.5} cy={-f.size * 0.1} r={f.size * 0.15} fill="white" />
            <circle cx={f.size * 0.55} cy={-f.size * 0.1} r={f.size * 0.07} fill="#111" />
            {/* Fin */}
            <path d={`M0,${-f.size*0.4} Q${f.size*0.3},${-f.size*0.8} ${f.size*0.6},${-f.size*0.4}`}
              fill={`hsla(${f.hue+20}, 75%, 70%, 0.7)`} />
          </g>
        ))}
      </svg>

      {/* Splashes + rainbow */}
      {splashes.map(s => (
        <div key={s.id} style={{ position: 'absolute', left: s.x, top: s.y, transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}>
          {/* Bioluminescent core glow */}
          <div style={{
            position: 'absolute', left: '50%', top: '50%',
            width: '70px', height: '70px',
            marginLeft: '-35px', marginTop: '-35px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0,255,210,0.75) 0%, rgba(0,180,220,0.4) 45%, rgba(0,100,200,0) 100%)',
            animation: 'bioGlow 2.2s ease-out forwards',
            filter: 'blur(7px)'
          }} />
          {/* Expanding rings */}
          {[0,1,2,3].map(i => (
            <div key={i} style={{
              position: 'absolute', left: '50%', top: '50%',
              width: `${44 + i * 28}px`, height: `${44 + i * 28}px`,
              marginLeft: `${-(22 + i * 14)}px`, marginTop: `${-(22 + i * 14)}px`,
              border: `${2 - i * 0.3}px solid hsla(${175 + i * 18}, 90%, 68%, 0.85)`,
              borderRadius: '50%',
              boxShadow: `0 0 6px hsla(${175 + i * 18}, 90%, 68%, 0.4)`,
              animation: `bioRipple 1.8s ease-out ${i * 0.18}s forwards`
            }} />
          ))}
          {/* Rising bioluminescent particles */}
          {[...Array(7)].map((_, i) => (
            <div key={i} style={{
              position: 'absolute', left: '50%', top: '50%',
              width: '7px', height: '7px',
              borderRadius: '50%',
              background: `hsla(${155 + i * 18}, 88%, 72%, 0.95)`,
              boxShadow: `0 0 5px hsla(${155 + i * 18}, 88%, 72%, 0.6)`,
              transform: `translate(${Math.round(Math.cos(i/7*Math.PI*2)*28) - 4}px, ${Math.round(Math.sin(i/7*Math.PI*2)*28) - 4}px)`,
              animation: `bioParticle 1.9s ease-out ${i * 0.09}s forwards`,
              filter: 'blur(0.5px)'
            }} />
          ))}
        </div>
      ))}

      {/* Hint */}
      {showHint && (
        <div style={{
          position: 'fixed', bottom: '160px', left: '50%', transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, rgba(0,60,120,0.92), rgba(0,100,180,0.92))',
          backdropFilter: 'blur(16px)',
          borderRadius: '16px', padding: '14px 22px',
          border: '1px solid rgba(100,200,255,0.3)',
          color: 'rgba(200,240,255,0.95)', fontSize: '13px',
          boxShadow: '0 8px 32px rgba(0,80,160,0.4)',
          animation: 'hintFadeIn 0.4s ease',
          pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 99999
        }}>
          🌊 ЛКМ — рыбки · ПКМ — пузырьки · ПКМ×2 — свечение · Wheel — течение
        </div>
      )}
    </div>
  );
};

export default OceanMode;
